<template>
  <WdsModal :show="show" :title="$t('Unlink from this client?')" @cancel="emit('cancel')">
    <WdsText>
      {{
        $t(
          ':supplier stops being contracted for :client. The link and its history are kept, and linking again revives it.',
          { supplier: link.supplierName, client: clientName },
        )
      }}
    </WdsText>
    <WdsAlert v-if="serverError" error data-test="supplier-link-unlink-error">
      {{ serverError }}
    </WdsAlert>
    <template #footer>
      <WdsStack direction-row gap-sm justify-end>
        <WdsButton secondary @click="emit('cancel')">{{ $t('Cancel') }}</WdsButton>
        <WdsButton
          error
          :disabled="removing"
          data-test="supplier-link-unlink-confirm"
          @click="unlink"
        >
          <WdsSpinner v-if="removing" :size="16" indeterminate />
          <template v-else>{{ $t('Unlink') }}</template>
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButton,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import type { PendingSupplierUnlink } from '@/components/Clients/supplierLinkUnlink'
import { SUPPLIER_LINK_QUERIES } from '@/components/Clients/supplierLinkQueries'

const UPDATE_SUPPLIER_CLIENT_LINKS = graphql(`
  mutation UpdateSupplierClientLinks($input: UpdateSupplierClientLinksInput!) {
    updateSupplierClientLinks(input: $input) {
      id
      updatedAt
      # Returned so the cached CompanyRecruiter.endClients the agency picker reads updates too.
      companyRecruiter {
        id
        endClients(first: 100) {
          data {
            id
            client {
              id
              name
            }
          }
          paginatorInfo {
            total
          }
        }
      }
    }
  }
`)

const props = defineProps<{
  show: boolean
  link: PendingSupplierUnlink
  clientName: string
}>()

const emit = defineEmits<{ (e: 'cancel'): void; (e: 'removed'): void }>()

const {
  mutate,
  loading: removing,
  error: mutationError,
} = useMutation(UPDATE_SUPPLIER_CLIENT_LINKS, {
  clientId: 'publicApi',
  refetchQueries: SUPPLIER_LINK_QUERIES,
})

const serverError: ComputedRef<string> = computed(
  () => mutationError.value?.graphQLErrors?.[0]?.message ?? '',
)

async function unlink() {
  await mutate(
    { input: { ids: [props.link.linkId], linked: false } },
    { context: { skipGlobalErrorHandling: true } },
  ).catch(() => null)

  if (!mutationError.value) {
    emit('removed')
  }
}
</script>
